import { Action } from '@reduxjs/toolkit';
import { Epic, combineEpics } from 'redux-observable';
import { combineLatest, distinctUntilChanged, filter, map } from 'rxjs';

import catalogs from 'domains/catalogs';
import session from 'domains/session';
import photos from 'domains/photos';
import navigation from 'domains/navigation';
import cartProducts from 'domains/cart_products';
import cart from 'domains/cart';
import cartBilling from 'domains/cart_billing';

import actions from './application.actions';

import type { RootState } from 'store/wos_store';

/** Kick off session check when app inits */
export const initAppSessionCheck: Epic<Action, Action> = (action$) =>
  action$.pipe(
    filter(actions.initApp.match),
    map(() => session.actions.getSession.pending())
  );

/** Kick off shallow catalog fetch when app inits */
export const initAppGetCatalogsEpic: Epic<Action, Action> = (action$) =>
  action$.pipe(
    filter(actions.initApp.match),
    map(() => catalogs.actions.getAllShallow.pending())
  );

/** Kick off cart guid check when app inits */
export const initAppCheckCartEpic: Epic<Action, Action> = (action$) =>
  action$.pipe(
    filter(actions.initApp.match),
    map(() => cart.actions.check.pending())
  );

/** Kick off photos fetch when app inits */
export const initAppGetPhotosEpic: Epic<Action, Action> = (action$) =>
  action$.pipe(
    filter(actions.initApp.match),
    map(() => photos.actions.getAll.pending())
  );
/**
 * Fetch cart products if we are showing the cart page, and have a cart
 */
export const navigateToCartEpic: Epic<Action, Action, RootState> = (action$) =>
  combineLatest([
    action$.pipe(
      filter(
        (a) =>
          (navigation.actions.initializeNavigation.match(a) && !!a.payload.cart) ||
          (navigation.actions.updateCurrentRoute.match(a) && a.payload === 'cart')
      )
    ),
    action$.pipe(filter(cart.actions.get.fulfilled.match)),
  ]).pipe(
    filter(([_, { payload }]) => !!payload && payload.product_ids.length > 0),
    map(() => cartProducts.actions.getAll.pending())
  );

/**
 * Initialize fetch for cart billing if we are showing the cart page, and have a cart
 */
export const updateCartBillingEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  state$.pipe(
    map((state) => ({
      showCart: navigation.selectors.showCart(state),
      hasCartGuid: cart.selectors.hasGuid(state),
    })),
    filter(({ showCart, hasCartGuid }) => showCart && hasCartGuid),
    distinctUntilChanged((prev) => prev.showCart),
    map(() => cartBilling.actions.get.pending())
  );

export default combineEpics(
  initAppSessionCheck,
  initAppGetCatalogsEpic,
  initAppCheckCartEpic,
  initAppGetPhotosEpic,
  navigateToCartEpic,
  updateCartBillingEpic
);
